import _toConsumableArray from "@babel/runtime/helpers/toConsumableArray";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import { useQuery } from 'urql';
import * as Sentry from '@sentry/browser';
import PropTypes from 'prop-types';
import { getInOptimizeVariant } from './utils';
import { OPTIMIZE_TED_VIDEO_ID, OPTIMIZE_TED_VIDEO_DATA } from './constants';
import { OV_ALERT, TEXT } from '../../config/constants';
import css from './Product.module.scss';
import Product from './Product';
import PRODUCT_QUERY from './queries';
import { OV_CDN } from '../../config/theme';
import { jsx as _jsx } from "react/jsx-runtime";

var goTo404 = function goTo404() {
  return window.location.assign('/404');
};

var ProductLoader = function ProductLoader(_ref) {
  var _productShopify$pdp, _colorways$find;

  var colorwayHandle = _ref.colorwayHandle,
      forSale = _ref.forSale,
      handle = _ref.handle,
      id = _ref.id,
      loading = _ref.loading,
      split = _ref.split,
      sizeSelected = _ref.sizeSelected,
      shopifyVariantId = _ref.shopifyVariantId;

  var _useQuery = useQuery({
    query: PRODUCT_QUERY,
    variables: {
      shopifyId: id,
      handle: handle
    }
  }),
      _useQuery2 = _slicedToArray(_useQuery, 1),
      _useQuery2$ = _useQuery2[0],
      data = _useQuery2$.data,
      error = _useQuery2$.error,
      fetching = _useQuery2$.fetching;

  if (fetching) {
    return /*#__PURE__*/_jsx("div", {
      "aria-busy": "true",
      className: css.loading,
      dangerouslySetInnerHTML: {
        __html: loading
      }
    });
  }

  if (error) {
    var event = new CustomEvent(OV_ALERT, {
      detail: TEXT.alert
    });
    var _window = window,
        dispatchEvent = _window.dispatchEvent;
    var graphQLErrors = error.graphQLErrors,
        networkError = error.networkError;
    console.error(networkError, graphQLErrors);
    if (Sentry) Sentry.captureException(error);
    dispatchEvent(event);
    return false;
  }

  if (!data.productShopify) {
    goTo404();
    return false;
  }

  var productShopify = data.productShopify;
  var apiColorways = productShopify.colorways;
  var productKnowledgeSet = ((_productShopify$pdp = productShopify.pdp) === null || _productShopify$pdp === void 0 ? void 0 : _productShopify$pdp.productKnowledgeSet) || null;
  var colorways = apiColorways.edges.filter(function (_ref2) {
    var node = _ref2.node;
    return node.swatch !== null;
  });

  if (colorways.length === 0) {
    goTo404();
    return false;
  }

  var firstColorway = colorways[0].node; // Set the current variant to the first colorway's first variant

  var colorway = split ? ((_colorways$find = colorways.find(function (_ref3) {
    var node = _ref3.node;
    return node.handle === colorwayHandle;
  })) === null || _colorways$find === void 0 ? void 0 : _colorways$find.node) || firstColorway : firstColorway;
  var variant = {
    id: colorway.variantStacks[0].variant.id,
    colorway: {
      id: colorway.id
    }
  }; // If we have a variant in the URL, find the variant in the colorways

  if (shopifyVariantId) {
    colorways.forEach(function (_ref4) {
      var _ref4$node = _ref4.node,
          id = _ref4$node.id,
          variantStacks = _ref4$node.variantStacks;
      var stack = variantStacks.find(function (_ref5) {
        var vari = _ref5.variant;
        return vari.shopifyId === shopifyVariantId;
      });

      if (stack) {
        variant = _objectSpread(_objectSpread({}, stack.variant), {}, {
          id: stack.variant.id,
          colorway: {
            id: id
          },
          size: stack.size
        });
      }

      return;
    });
  }

  var VARIANT_AVAILABILITY = window.OV.VARIANT_AVAILABILITY;

  var getInventory = function getInventory(variantId, apiInventory) {
    return VARIANT_AVAILABILITY ? VARIANT_AVAILABILITY[variantId] : apiInventory;
  };

  _toConsumableArray(document.querySelectorAll('.nav-product-id')).forEach(function (link) {
    link.href = "".concat(link.getAttribute('data-url')).concat(productShopify.id);
  }); // Loop through the product and set availability based on the window's inventory object if available


  colorways.forEach(function (_ref6) {
    var node = _ref6.node;
    var variantStacks = node.variantStacks;
    var colorwayInventory = 0;
    variantStacks.forEach(function (_ref7) {
      var vari = _ref7.variant;
      var inventory = getInventory(vari.shopifyId, vari.inventoryQuantity);
      vari.inventoryQuantity = inventory;
      vari.available = inventory > 0; // Some sizes are over-sold, below 0: ensure not to "un-count" our inventory

      if (inventory > 0) {
        colorwayInventory += inventory;
      }
    });
    node.available = colorwayInventory > 0;
  });
  var swatches = colorways.map(function (_ref8) {
    var _ref8$node = _ref8.node,
        id = _ref8$node.id,
        color = _ref8$node.color,
        availableColor = _ref8$node.available,
        sizesAvailable = _ref8$node.sizesAvailable,
        swatch = _ref8$node.swatch;
    return {
      id: swatch.id,
      label: color,
      image: swatch.image,
      disabled: !availableColor,
      sizesAvailable: sizesAvailable.filter(function (size) {
        return size.available;
      }).map(function (size) {
        return size.size;
      }),
      value: id
    };
  });
  var colorwayNodes = colorways.map(function (_ref9) {
    var node = _ref9.node;
    return node;
  });

  if (getInOptimizeVariant(OPTIMIZE_TED_VIDEO_ID, '1')) {
    colorwayNodes.forEach(function (node, idx) {
      var asset = OPTIMIZE_TED_VIDEO_DATA[node.color.toLowerCase()];

      if (asset) {
        node.assets.edges[1].node = {
          id: 'asset' + idx,
          image: null,
          type: 'VIEWS',
          video: {
            id: 'video' + idx,
            adminTitle: 'The Exercise Dress Product Video',
            autoplay: true,
            controls: false,
            loop: true,
            muted: true,
            poster: {
              id: idx,
              alt: 'A person wearing the exercise dress',
              height: 1080,
              src: asset.image,
              width: 1920
            },
            src: asset.video,
            track: "".concat(OV_CDN, "video/silent-video.vtt"),
            title: 'The Exercise Dress Product Video'
          }
        };
      }
    });
  }

  return /*#__PURE__*/_jsx(Product, {
    colorways: colorwayNodes,
    forSale: forSale,
    product: productShopify,
    productKnowledgeSet: productKnowledgeSet,
    sizeSelected: sizeSelected,
    swatches: swatches,
    variant: variant
  });
};

ProductLoader.defaultProps = {
  colorwayHandle: '',
  forSale: true,
  handle: '',
  id: '',
  loading: '',
  shopifyVariantId: false,
  split: false
};
ProductLoader.propTypes = {
  colorwayHandle: PropTypes.string,
  forSale: PropTypes.bool,
  handle: PropTypes.string,
  id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  loading: PropTypes.string,
  shopifyVariantId: PropTypes.oneOfType([PropTypes.number, PropTypes.string, PropTypes.bool]),
  sizeSelected: PropTypes.bool,
  split: PropTypes.bool
};
export default ProductLoader;